import { useList, useMountEffect } from "@react-hookz/web";
import React, { useCallback, useEffect, useState } from "react";
import { getLoginDialogTitleAndDescription } from "~/components/acquisition-popup-container/display/helpers";
import { CloseButton } from "~/components/acquisition-popup-container/display/popups/common/close-button";
import { PopupBackdrop } from "~/components/acquisition-popup-container/display/styles";
import { useSdkContext } from "~/components/contexts";
import { FollowButtonAction, FollowType } from "~/components/follow-button/enums";
import { Modal } from "~/components/modal";
import { queryFromConfig } from "./actions";
import { Button, CheckboxContainer, ConfirmationContainer, Container, Description, Item, ItemLabel, ManageFollowAnchor, SelectionContainer, StyledIconAdd, StyledIconChecked, StyledIconConfirmation, ThankYouTitle, Title } from "./styles";
export const TopicPackageFollowPopup = ({
  confirmedConfig,
  modalContainerReference,
  onClose,
  topicPackageFollow
}) => {
  const selectionConfig = topicPackageFollow.stepForm.step1;
  const confirmationConfig = topicPackageFollow.stepForm.step2;
  const {
    displayName,
    key,
    shouldShowThankYouPopup,
    trackingActionName
  } = topicPackageFollow;
  const {
    config,
    handleFollow,
    handleOpenLoginDialog,
    handleRoutePush,
    handleTrackGA,
    handleTrackGA4,
    user
  } = useSdkContext();
  const campaignName = trackingActionName;
  useMountEffect(() => {
    if (confirmedConfig) {
      if (shouldShowThankYouPopup) {
        handleTrackGA4({
          action: "imp",
          category: "promopopup",
          customized_parameters: {
            cta_type: "follow",
            internal_campaign: campaignName,
            step: "2"
          },
          subcategory: ""
        });
      }
      handleFollowConfirm();
      if (!shouldShowThankYouPopup) {
        onClose();
      }
      return;
    }
    handleTrackGA({
      action: `Acquisition/${campaignName}/Impression`,
      category: "Acquisition",
      label: window.location.href
    });
    handleTrackGA4({
      action: "imp",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        step: "1"
      },
      subcategory: ""
    });
  });
  const handleCloseClick = () => {
    handleTrackGA({
      action: `Acquisition/${campaignName}/Close/Click`,
      category: "Acquisition",
      label: window.location.href
    });
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        message: "close button",
        step: "1"
      },
      subcategory: ""
    });
    onClose();
  };
  const [itemList, itemListActions] = useList(selectionConfig.topicPackageDefaultOptionList.map((item, index) => confirmedConfig !== null && confirmedConfig !== void 0 && confirmedConfig.checkedItemIndex ? {
    ...item,
    checked: confirmedConfig.checkedItemIndex.includes(index)
  } : {
    ...item,
    checked: item.defaultChecked
  }));
  useEffect(() => {
    itemListActions.reset();
  }, [itemListActions, selectionConfig.topicPackageDefaultOptionList, confirmedConfig === null || confirmedConfig === void 0 ? void 0 : confirmedConfig.checkedItemIndex]);
  const handleItemClick = index => {
    const item = itemList[index];
    itemListActions.updateAt(index, {
      ...item,
      checked: !item.checked
    });
  };
  const [hasConfirmed, setHasConfirmed] = useState((confirmedConfig === null || confirmedConfig === void 0 ? void 0 : confirmedConfig.checkedItemIndex) && confirmedConfig.checkedItemIndex.length > 0);
  const handleFollowConfirm = useCallback(() => {
    if (!user) {
      const checkedIndex = itemList.reduce((list, item, index) => {
        if (item.checked) list.push(index);
        return list;
      }, []);
      const destination = new URL(window.location.origin + window.location.pathname);
      destination.search = queryFromConfig({
        checkedIndex,
        configKey: key
      });
      const {
        description,
        title
      } = getLoginDialogTitleAndDescription({
        campaignName: displayName,
        type: "topics"
      });
      handleOpenLoginDialog({
        description,
        destination: destination.toString(),
        ga4CustomParameter: {
          internal_campaign: campaignName,
          trigger_point: "ppto"
        },
        title
      });
      return;
    }
    const checkedItemList = itemList.filter(item => item.checked);
    handleFollow({
      action: FollowButtonAction.Follow,
      callback: () => {
        const checkedLabel = checkedItemList.map(item => item.optionLabel).join(", ");
        handleTrackGA({
          action: `Acquisition/${campaignName}/Follow/Topic Package`,
          category: "Acquisition",
          label: checkedLabel
        });
        handleTrackGA4({
          action: "sys",
          category: "follow",
          customized_parameters: {
            action_type: "follow",
            follow_type: "topic_package",
            internal_campaign: campaignName,
            package_name: checkedLabel,
            topic_id: checkedItemList.flatMap(item => item.topicIdList).join(", "),
            trigger_point: "ppto"
          },
          subcategory: "result"
        });
      },
      entityIds: checkedItemList.flatMap(item => item.topicIdList),
      followType: FollowType.Topic
    });
    setHasConfirmed(true);
  }, [campaignName, displayName, handleFollow, handleOpenLoginDialog, handleTrackGA, handleTrackGA4, itemList, key, user]);
  const shouldDisableConfirmButton = itemList.every(item => !item.checked);
  const handleConfirmSelectionClick = () => {
    if (shouldDisableConfirmButton) return;
    handleTrackGA({
      action: `Acquisition/${campaignName}/Follow/Click`,
      category: "Acquisition",
      label: window.location.href
    });
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        message: selectionConfig.buttonText,
        step: "1"
      },
      subcategory: ""
    });
    handleFollowConfirm();
    if (!shouldShowThankYouPopup) {
      onClose();
    }
  };
  const handleConfirmContinueClick = () => {
    handleTrackGA({
      action: `Acquisition/${campaignName}/Continue Reading/Click`,
      category: "Acquisition",
      label: window.location.href
    });
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        internal_campaign: campaignName,
        message: confirmationConfig.buttonText,
        step: "2"
      },
      subcategory: ""
    });
    onClose();
  };
  const handleManageFollowAnchorClick = event => {
    event === null || event === void 0 || event.preventDefault();
    handleTrackGA({
      action: `Acquisition/${campaignName}/Manage following/Click`,
      category: "Acquisition",
      label: window.location.href
    });
    const manageFollowRelativeUrlPath = "/manage/following?module=topic_package&pgtype=article";
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "follow",
        destination_url: manageFollowRelativeUrlPath,
        internal_campaign: campaignName,
        message: confirmationConfig.bottomLinkText,
        step: "2"
      },
      subcategory: ""
    });
    const manageFollowUrl = `${config.accounts.host}${manageFollowRelativeUrlPath}`;
    void handleRoutePush(manageFollowUrl, {
      isExternal: true
    });
  };
  if (!shouldShowThankYouPopup && hasConfirmed) return null;
  return /*#__PURE__*/React.createElement(Modal, {
    BackdropComponent: PopupBackdrop,
    container: modalContainerReference === null || modalContainerReference === void 0 ? void 0 : modalContainerReference.current,
    fadeTimeout: 400,
    open: true,
    "data-qa": "topic-package-follow-popup"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(CloseButton, {
    onClose: handleCloseClick
  }), hasConfirmed ? /*#__PURE__*/React.createElement(ConfirmationContainer, null, /*#__PURE__*/React.createElement(StyledIconConfirmation, null), /*#__PURE__*/React.createElement(ThankYouTitle, {
    dangerouslySetInnerHTML: {
      __html: confirmationConfig.title
    }
  }), /*#__PURE__*/React.createElement(Description, {
    dangerouslySetInnerHTML: {
      __html: confirmationConfig.description
    }
  }), /*#__PURE__*/React.createElement(Button, {
    dangerouslySetInnerHTML: {
      __html: confirmationConfig.buttonText
    },
    onClick: handleConfirmContinueClick
  }), /*#__PURE__*/React.createElement(ManageFollowAnchor, {
    dangerouslySetInnerHTML: {
      __html: confirmationConfig.bottomLinkText
    },
    onClick: handleManageFollowAnchorClick
  })) : /*#__PURE__*/React.createElement(SelectionContainer, null, /*#__PURE__*/React.createElement(Title, {
    dangerouslySetInnerHTML: {
      __html: selectionConfig.title
    }
  }), /*#__PURE__*/React.createElement(CheckboxContainer, null, selectionConfig.topicPackageDefaultOptionList.map((item, index) => /*#__PURE__*/React.createElement(Item, {
    $isChecked: itemList[index].checked,
    key: index,
    onClick: () => handleItemClick(index)
  }, /*#__PURE__*/React.createElement(StyledIconAdd, null), /*#__PURE__*/React.createElement(ItemLabel, {
    dangerouslySetInnerHTML: {
      __html: item.optionLabel
    }
  }), /*#__PURE__*/React.createElement(StyledIconChecked, null)))), /*#__PURE__*/React.createElement(Description, {
    dangerouslySetInnerHTML: {
      __html: selectionConfig.description
    }
  }), /*#__PURE__*/React.createElement(Button, {
    $isDisabled: shouldDisableConfirmButton,
    dangerouslySetInnerHTML: {
      __html: selectionConfig.buttonText
    },
    onClick: handleConfirmSelectionClick
  }))));
};